import { LearnInstance } from '@bb-learn-instances-service/common';
import { SiteTypeFilter, matchesSiteTypeFilter } from 'utils/siteType';

/**
 * The optional filters on the Learn Sites list, modelled on the Captain Learn list: each one is
 * switched on from the Filters menu, and a filter that is switched off does not narrow the list.
 */
export const LEARN_SITE_FILTER_KEYS = [
  'createdFrom',
  'createdTo',
  'status',
  'fleet',
  'siteType',
  'database',
  'locked',
] as const;

export type LearnSiteFilterKey = (typeof LEARN_SITE_FILTER_KEYS)[number];

export const DEFAULT_ENABLED_FILTERS: LearnSiteFilterKey[] = ['status', 'siteType'];

export const DATABASE_FILTERS = ['all', 'shared', 'dedicated'] as const;

export type DatabaseFilter = (typeof DATABASE_FILTERS)[number];

export const LOCK_FILTERS = ['all', 'locked', 'unlocked'] as const;

export type LockFilter = (typeof LOCK_FILTERS)[number];

/** Lock state by tenant id: undefined until read, null when it could not be read */
export type LockStateMap = Record<string, boolean | null | undefined>;

export interface LearnSiteFilterValues {
  /** yyyy-mm-dd, inclusive */
  createdFrom: string;
  /** yyyy-mm-dd, inclusive */
  createdTo: string;
  status: string;
  fleet: string;
  siteType: SiteTypeFilter;
  database: DatabaseFilter;
  locked: LockFilter;
}

export const DEFAULT_FILTER_VALUES: LearnSiteFilterValues = {
  createdFrom: '',
  createdTo: '',
  status: 'Installed',
  fleet: 'All',
  siteType: 'all',
  database: 'all',
  locked: 'all',
};

/** Shared database id keyed by the Learn site tenant id, from each fleet's shared RDS list. */
export type SharedDatabaseMap = Record<string, string>;

export interface DatabaseLookup {
  sharedDatabases: SharedDatabaseMap;
  /** True once every shared RDS fleet in the region answered */
  loaded: boolean;
}

export interface SiteDatabase {
  type: 'shared' | 'dedicated' | 'unknown';
  /** The shared RDS instance identifier, when it is known */
  name?: string;
}

// A shared RDS drops a site from its list when the site is decommissioned, so for these
// statuses a site missing from every list may still have been on a shared RDS.
const DECOMMISSION_STATUSES = [
  'Decommissioned',
  'DecommissionedQueued',
  'DecommissionInProgress',
  'DecommissionFailed',
  'WaitDecommission',
];

export const getSharedDatabaseId = (
  site: Pick<LearnInstance, 'LearnSiteTenantId' | 'TenantId'>,
  sharedDatabases: SharedDatabaseMap,
): string | undefined =>
  (site.LearnSiteTenantId && sharedDatabases[site.LearnSiteTenantId]) ||
  (site.TenantId && sharedDatabases[site.TenantId]) ||
  undefined;

/**
 * The create site job input saved on the instance record. Not in the LearnInstance type, and
 * absent on older records, but unlike the shared RDS lists it is kept after decommissioning.
 */
export interface SiteDatabaseSource {
  IsSharedRds?: boolean;
  SharedRdsId?: string;
}

const isSharedRdsId = (id?: string): id is string => Boolean(id && /-shared-rds$/.test(id));

/**
 * The database a site uses. Shared RDS ids are `<fleet>-<uuid>-shared-rds` and come from the
 * instance record or the fleet's shared RDS list. A dedicated RDS is only reported as dedicated:
 * its name would have to be derived from a naming convention, so it is not shown as fact.
 */
export const getSiteDatabase = (
  site: Pick<
    LearnInstance,
    'LearnSiteTenantId' | 'TenantId' | 'FleetId' | 'InstallationId' | 'Status'
  > &
    SiteDatabaseSource,
  { sharedDatabases, loaded }: DatabaseLookup,
): SiteDatabase => {
  const sharedDatabaseId =
    getSharedDatabaseId(site, sharedDatabases) ||
    (isSharedRdsId(site.SharedRdsId) ? site.SharedRdsId : undefined);
  if (sharedDatabaseId) {
    return { type: 'shared', name: sharedDatabaseId };
  }
  // Some records hold a message such as "No shared RDS instance available" in SharedRdsId
  if (site.IsSharedRds === true) {
    return { type: 'shared' };
  }
  if (!site.FleetId) {
    return { type: 'unknown' };
  }
  const knownDedicated = site.IsSharedRds === false;
  if (!knownDedicated && (!loaded || DECOMMISSION_STATUSES.includes(site.Status))) {
    return { type: 'unknown' };
  }
  return { type: 'dedicated' };
};

// `created` is epoch seconds; the date inputs are UTC calendar days, as Captain shows them.
const startOfDaySeconds = (day: string) => Date.parse(`${day}T00:00:00Z`) / 1000;
const endOfDaySeconds = (day: string) => Date.parse(`${day}T23:59:59Z`) / 1000;

export const matchesLearnSiteFilters = (
  site: LearnInstance,
  enabled: ReadonlySet<LearnSiteFilterKey>,
  values: LearnSiteFilterValues,
  databases: DatabaseLookup,
  locks: LockStateMap = {},
): boolean => {
  const created = Number(site.created);

  if (enabled.has('createdFrom') && values.createdFrom) {
    if (!created || created < startOfDaySeconds(values.createdFrom)) {
      return false;
    }
  }

  if (enabled.has('createdTo') && values.createdTo) {
    if (!created || created > endOfDaySeconds(values.createdTo)) {
      return false;
    }
  }

  if (enabled.has('status') && values.status !== 'All' && site.Status !== values.status) {
    return false;
  }

  if (enabled.has('fleet') && values.fleet !== 'All' && site.FleetId !== values.fleet) {
    return false;
  }

  if (enabled.has('siteType') && !matchesSiteTypeFilter(site, values.siteType)) {
    return false;
  }

  if (enabled.has('database') && values.database !== 'all') {
    if (getSiteDatabase(site, databases).type !== values.database) {
      return false;
    }
  }

  // A site whose lock state is not read yet (or could not be) matches neither Locked nor Unlocked;
  // the page says how many sites that leaves out (see countUncheckedLocks)
  if (enabled.has('locked') && values.locked !== 'all') {
    const locked = locks[site.TenantId];
    if (typeof locked !== 'boolean' || locked !== (values.locked === 'locked')) {
      return false;
    }
  }

  return true;
};

/** Locked and Unlocked need every loaded site's lock state, which is read site by site. */
export const needsLockStates = (
  enabled: ReadonlySet<LearnSiteFilterKey>,
  values: LearnSiteFilterValues,
): boolean => enabled.has('locked') && values.locked !== 'all';

export interface UncheckedLocks {
  /** Sites whose lock state is still being read */
  pending: number;
  /** Sites whose lock state could not be read */
  failed: number;
}

/** How many of the given tenants have no known lock state yet, or could not be checked. */
export const countUncheckedLocks = (tenantIds: string[], locks: LockStateMap): UncheckedLocks => ({
  pending: tenantIds.filter((tenantId) => locks[tenantId] === undefined).length,
  failed: tenantIds.filter((tenantId) => locks[tenantId] === null).length,
});
